import { Controller } from "@hotwired/stimulus";

import logger from "~/lib/logger";

const log = logger();

interface Session {
  legalEntityId: string;
  allowBankAccountFormatSelection: boolean;
  allowIntraRegionCrossBorderPayout: boolean;
  componentsEnvironment: string;
  componentsToken: string;
  locale: string;
}

interface AdyenComponentElement extends HTMLElement {
  locale: string;
  environment: string;
  rootlegalentityid: string;
  fetchToken: () => Promise<{ token: string }>;
  settings?: Record<string, unknown>;
}

export default class AdyenComponentsController extends Controller {
  static override values = {
    sessionUrl: String,
    successUrl: String,
    mode: String,
  };

  declare sessionUrlValue: string;
  declare successUrlValue: string;
  declare modeValue: "create" | "manage";

  override async connect(): Promise<void> {
    const session = await this.getSession();

    switch (this.modeValue) {
      case "create":
        await this.mountConfigurationComponent(session);
        break;
      case "manage":
        await this.mountManagementComponent(session);
        break;
      default:
        log.error("Invalid Adyen components mode", this.modeValue);
        break;
    }
  }

  private async mountConfigurationComponent(session: Session): Promise<void> {
    await import("@adyen/kyc-components/transfer-instrument-configuration");

    const el = document.createElement(
      "adyen-transfer-instrument-configuration",
    ) as AdyenComponentElement;
    el.locale = session.locale;
    el.environment = session.componentsEnvironment;
    el.rootlegalentityid = session.legalEntityId;
    el.fetchToken = this.fetchToken.bind(this);
    el.settings = {
      allowIntraRegionCrossBorderPayout:
        session.allowIntraRegionCrossBorderPayout,
      allowBankAccountFormatSelection: session.allowBankAccountFormatSelection,
    };

    el.addEventListener("complete", () => {
      fetch(this.successUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (response.ok) {
            window.location.href = response.url;
            return;
          }
          throw new Error("Network response was not ok");
        })
        .catch((error) => {
          log.error("Adyen transfer instrument configuration failed:", error);
        });
    });

    this.element.appendChild(el);
  }

  private async mountManagementComponent(session: Session): Promise<void> {
    await import("@adyen/kyc-components/transfer-instrument-management");

    const el = document.createElement(
      "adyen-transfer-instrument-management",
    ) as AdyenComponentElement;
    el.locale = session.locale;
    el.environment = session.componentsEnvironment;
    el.rootlegalentityid = session.legalEntityId;
    el.fetchToken = this.fetchToken.bind(this);

    el.addEventListener("add", () => {
      throw new Error("Not implemented");
    });
    el.addEventListener("edit", () => {
      throw new Error("Not implemented");
    });
    el.addEventListener("remove", () => {
      throw new Error("Not implemented");
    });

    this.element.appendChild(el);
  }

  private async getSession(): Promise<Session> {
    const response = await fetch(this.sessionUrlValue);
    return await response.json();
  }

  private async fetchToken(): Promise<{ token: string }> {
    const session = await this.getSession();
    return { token: session.componentsToken };
  }
}
